import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Search, BookOpen, Video, Image, FileText, QrCode, ChevronRight, GraduationCap, DollarSign, CreditCard, ClipboardList, Ruler } from "lucide-react";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import { getSessionToken } from "@/utils/auth";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  "training": GraduationCap,
  "pricing": DollarSign,
  "payments": CreditCard,
  "policies": ClipboardList,
  "reference": Ruler,
  "default": BookOpen
};

const CONTENT_TYPE_ICONS = {
  "video": Video,
  "image": Image,
  "pdf": FileText,
  "text": FileText,
  "qr": QrCode
};

export default function Resources() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedResource, setSelectedResource] = useState(null);

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (user) {
      fetchCategories();
      fetchResources();
    }
  }, [user, selectedCategory, searchQuery]);

  const fetchUser = async () => {
    try {
      const token = getSessionToken();
      if (!token) {
        navigate("/login");
        return;
      }
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/resource-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchResources = async () => {
    try {
      const token = getSessionToken();
      const params = {};
      if (selectedCategory) params.category_id = selectedCategory;
      if (searchQuery) params.search = searchQuery;
      
      const response = await axios.get(`${API}/resources`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setResources(response.data);
    } catch (error) {
      console.error("Failed to fetch resources:", error);
    }
  };

  const getCategoryIcon = (category) => {
    const iconKey = category.icon || category.name.toLowerCase().replace(/[^a-z]/g, '');
    const Icon = CATEGORY_ICONS[iconKey] || CATEGORY_ICONS.default;
    return Icon;
  };

  const getContentTypeIcon = (type) => {
    const Icon = CONTENT_TYPE_ICONS[type] || FileText;
    return Icon;
  };

  const renderResourceContent = (resource) => {
    switch (resource.content_type) {
      case "video":
        // Support YouTube, Vimeo embeds, and direct video files
        let embedUrl = resource.content_url;
        let isDirectVideo = false;
        
        if (embedUrl.includes("youtube.com/watch")) {
          const videoId = new URL(embedUrl).searchParams.get("v");
          embedUrl = `https://www.youtube.com/embed/${videoId}`;
        } else if (embedUrl.includes("youtube.com/shorts/")) {
          // YouTube Shorts - extract video ID from /shorts/VIDEO_ID
          const videoId = embedUrl.split("youtube.com/shorts/")[1].split("?")[0].split("/")[0];
          embedUrl = `https://www.youtube.com/embed/${videoId}`;
        } else if (embedUrl.includes("youtu.be/")) {
          const videoId = embedUrl.split("youtu.be/")[1].split("?")[0];
          embedUrl = `https://www.youtube.com/embed/${videoId}`;
        } else if (embedUrl.includes("vimeo.com/")) {
          const videoId = embedUrl.split("vimeo.com/")[1].split("?")[0];
          embedUrl = `https://player.vimeo.com/video/${videoId}`;
        } else if (embedUrl.match(/\.(mp4|webm|ogg|mov)(\?|$)/i)) {
          // Direct video file
          isDirectVideo = true;
        } else if (embedUrl.includes("drive.google.com")) {
          // Google Drive video - convert to preview URL
          const fileIdMatch = embedUrl.match(/\/d\/([^\/]+)/);
          if (fileIdMatch) {
            embedUrl = `https://drive.google.com/file/d/${fileIdMatch[1]}/preview`;
          }
        }
        
        if (isDirectVideo) {
          return (
            <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
              <video
                src={embedUrl}
                className="w-full h-full"
                controls
                playsInline
                preload="metadata"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          );
        }
        
        return (
          <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
            <iframe
              src={embedUrl}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={resource.title}
            />
          </div>
        );
      
      case "image":
        return (
          <img
            src={resource.content_url}
            alt={resource.title}
            className="w-full rounded-lg"
          />
        );
      
      case "pdf":
        return (
          <div className="space-y-4">
            <a
              href={resource.content_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-3 bg-[#207024] text-white rounded-lg font-semibold"
            >
              <FileText className="w-5 h-5" />
              Open PDF
            </a>
            <iframe
              src={resource.content_url}
              className="w-full h-96 rounded-lg border"
              title={resource.title}
            />
          </div>
        );
      
      case "qr":
        return (
          <div className="flex flex-col items-center gap-4">
            <img
              src={resource.content_url}
              alt={resource.title}
              className="w-64 h-64 rounded-lg"
            />
            {resource.content_text && (
              <a
                href={resource.content_text}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#207024] font-semibold underline"
              >
                Open Link
              </a>
            )}
          </div>
        );
      
      case "text":
      default:
        return (
          <div className="prose prose-sm max-w-none">
            <div className="whitespace-pre-wrap text-zinc-700">
              {resource.content_text}
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#207024]"></div>
      </div>
    );
  }

  // Resource detail view
  if (selectedResource) {
    return (
      <div className="min-h-screen bg-zinc-50 pb-20">
        <Header user={user} />
        
        <div className="max-w-2xl mx-auto px-4 py-6">
          <button
            onClick={() => setSelectedResource(null)}
            className="flex items-center gap-2 text-zinc-600 mb-4 hover:text-zinc-900"
          >
            <ChevronRight className="w-5 h-5 rotate-180" />
            Back to Resources
          </button>
          
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-start gap-3 mb-4">
              {(() => {
                const Icon = getContentTypeIcon(selectedResource.content_type);
                return <Icon className="w-6 h-6 text-[#207024] flex-shrink-0 mt-1" />;
              })()}
              <div>
                <h1 className="text-xl font-bold text-zinc-900">{selectedResource.title}</h1>
                {selectedResource.description && (
                  <p className="text-zinc-600 mt-1">{selectedResource.description}</p>
                )}
              </div>
            </div>
            
            <div className="mt-6">
              {renderResourceContent(selectedResource)}
            </div>
          </div>
        </div>
        
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      <Header user={user} />
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="font-barlow text-2xl font-bold text-zinc-900 uppercase mb-6">
          Resource Hub
        </h1>
        
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
          <input
            type="text"
            placeholder="Search resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-12 pl-12 pr-4 rounded-xl border-2 border-zinc-200 focus:border-[#207024] text-base"
            data-testid="resource-search"
          />
        </div>
        
        {/* Categories */}
        {!searchQuery && (
          <div className="mb-6">
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  !selectedCategory
                    ? "bg-[#207024] text-white"
                    : "bg-white border-2 border-zinc-200 text-zinc-600 hover:border-zinc-300"
                }`}
              >
                All
              </button>
              {categories.map((cat) => {
                const Icon = getCategoryIcon(cat);
                return (
                  <button
                    key={cat.category_id}
                    onClick={() => setSelectedCategory(cat.category_id)}
                    className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                      selectedCategory === cat.category_id
                        ? "bg-[#207024] text-white"
                        : "bg-white border-2 border-zinc-200 text-zinc-600 hover:border-zinc-300"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Resources List */}
        <div className="space-y-3">
          {resources.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              {searchQuery ? "No resources found" : "No resources yet"}
            </div>
          ) : (
            resources.map((resource) => {
              const Icon = getContentTypeIcon(resource.content_type);
              return (
                <button
                  key={resource.resource_id}
                  onClick={() => setSelectedResource(resource)}
                  className="w-full bg-white rounded-xl border-2 border-zinc-100 p-4 flex items-center gap-4 hover:border-zinc-200 transition-colors text-left"
                  data-testid={`resource-${resource.resource_id}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-[#207024]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-zinc-900 truncate">{resource.title}</h3>
                    {resource.description && (
                      <p className="text-sm text-zinc-500 truncate">{resource.description}</p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                </button>
              );
            })
          )}
        </div>
        
        {/* Admin Link */}
        {user?.role === "Admin" && (
          <div className="mt-8 pt-6 border-t">
            <button
              onClick={() => navigate("/settings/resources")}
              className="w-full py-3 text-center text-[#207024] font-semibold hover:underline"
            >
              Manage Resources →
            </button>
          </div>
        )}
      </div>
      
      <BottomNav />
    </div>
  );
}
